import { Card, CardBody, CardTitle } from '../Card';
import React, { useEffect, useState } from 'react';
import { Button } from '../buttons/Button';
import { ArrowForward, Send } from '@material-ui/icons';
import { Input } from '../form/Input';
import { FormField } from '../form/FormField';
import { Label } from '../form/Label';
import { Link } from '../Link';
import { CircularProgress, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Colors } from '../../constants/colors';
import { AlignContent } from '../helpers/AlignContent';
import { selectRequestType } from '../../store/auth/auth.selector';
import { useDispatch, useSelector } from 'react-redux';
import { Header } from '../Header';
import { Container } from '../helpers/Container';
import { RequestType } from '../../model/enums/RequestType';
import { enrollForVisit } from '../../store/auth/actions/enrollForVisit';
import { enrollForReceipt } from '../../store/auth/actions/enrollForReceipt';
import { logInWithCode } from '../../store/auth/actions/logInWithCode';
import { Error } from '../Error';
import { getApiErrorMessage } from '../../hooks/getApiErrorMessage';
import { useNavigate } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import { validateEmail } from '../../store/registration/actions/validateEmail';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { PhoneFormField } from '../form/PhoneFormField';
import { EmailFormField } from '../form/EmailFormField';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paragraph: {
      margin: '0 0 30px',
      color: Colors.dark
    },
    helperNote: {
      color: Colors.dark,
      margin: '30px 0 0',
      lineHeight: 2,
      textAlign: 'center',
      fontSize: 14,
      '& button': {
        fontSize: 14
      }
    },
    verificationCard: {
      margin: '70px 0 0',
      minWidth: 420,
      [theme.breakpoints.down('sm')]: {
        minWidth: '100%',
        margin: '30px 0 0'
      }
    },
    continueButton: {
      width: '100%',
      marginTop: 20
    },
    verifyButton: {
      width: '100%',
      marginTop: 20
    },
    progressCircle: {
      color: Colors.white,
      marginLeft: 10
    }
  })
);

export const Verification = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const requestType = useSelector(selectRequestType);

  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [codeSent, setCodeSent] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [phoneValid, setPhoneValid] = useState<boolean>(false);
  const [emailValid, setEmailValid] = useState<boolean>(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    setErrorMessage('');
  }, [code, codeSent]);

  const handleReset = () => {
    setPhone('');
    setCode('');
    setCodeSent(false);
  };

  const handleEnrollForVisit = async () => {
    if (!requestType || !RequestType.isRegistration(requestType)) {
      navigate('/login', { replace: true });
      return;
    }

    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return;
    }

    setLoading(true);
    try {
      const captchaToken = await executeRecaptcha('enrollForVisit');
      await enrollForVisit(requestType, phone, captchaToken);
      setCodeSent(true);
    } catch (e) {
      setErrorMessage(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollForReceipt = async () => {
    if (RequestType.isRegistration(requestType)) {
      navigate('/login', { replace: true });
      return;
    }

    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return;
    }

    setLoading(true);
    try {
      const captchaToken = await executeRecaptcha('enrollForReceipt');
      await enrollForReceipt(phone, email, captchaToken);
      setCodeSent(true);
    } catch (e) {
      setErrorMessage(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeVerification = async () => {
    if (requestType === undefined) {
      navigate('/login', { replace: true });
      return;
    }

    setLoading(true);
    try {
      await dispatch(logInWithCode(code));

      if (requestType === RequestType.ReceiptOrder) {
        navigate('/receipt', { replace: true });
      } else {
        navigate('/registration', { replace: true });
      }
    } catch (e) {
      setErrorMessage(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const setAndValidatePhone = (phone: string) => {
    setPhone(phone);
    setPhoneValid(isValidPhoneNumber(phone));
  };

  const setAndValidateEmail = (email: string) => {
    setEmail(email);
    setEmailValid(validateEmail(email));
  };

  const enrollButton = requestType === RequestType.ReceiptOrder ? (
      <Button onClick={handleEnrollForReceipt}
              className={classes.continueButton}
              disabled={!phoneValid || !emailValid || loading}>
        Pokračovat
        {!loading ? <ArrowForward /> : <CircularProgress size={24} className={classes.progressCircle} />}
      </Button>)
    : (
      <Button onClick={handleEnrollForVisit}
              className={classes.continueButton}
              disabled={!phoneValid || loading}>
        Odeslat ověřovací kód
        {!loading ? <Send /> : <CircularProgress size={24} className={classes.progressCircle} />}
      </Button>
    );

  const cardBodyForEnroll = (
    <CardBody>
      {requestType !== RequestType.ReceiptOrder && <p className={classes.paragraph}>
        Pro úspěšné kontaktování ze strany IKEM je potřeba ověřit Vaše telefonní číslo.
      </p>}

      {requestType === RequestType.ReceiptOrder &&
        <EmailFormField required={true}
                        onChangeValid={(isValid: boolean) => setEmailValid(isValid)}
                        onChangeValue={(email: string) => setEmail(email)} />}

      <PhoneFormField required={true} onChangeValue={(phone: string) => setPhone(phone)}
                      onChangeValid={(isValid: boolean) => setPhoneValid(isValid)}
                      formHelperText={requestType !== RequestType.ReceiptOrder && 'Na toto číslo Vám bude odeslán ověřovací kód.'} />
      <Error errorMessage={errorMessage}
             onClose={() => setErrorMessage('')} />
      {enrollButton}
    </CardBody>
  );

  const cardBodyForCodeVerification = (
    <CardBody>
      <p className={classes.paragraph}>
        Na telefonní číslo <strong>{phone}</strong> Vám byl odeslán ověřovací kód. Opiště ho do polička níže a pokračujte kliknutím na
        tlačítko "Ověřit kód".
      </p>
      <FormField>
        <Label>Ověřovací kód z SMS:</Label>
        <Input onChange={e => setCode(e.target.value)}
               value={code}
               name='code' />
      </FormField>
      <Error errorMessage={errorMessage}
             onClose={() => setErrorMessage('')} />
      <Button onClick={handleCodeVerification}
              className={classes.verifyButton}
              disabled={!code.length || loading}>
        Ověřit kód
        {loading && <CircularProgress size={24} className={classes.progressCircle} />}
      </Button>
      <p className={classes.helperNote}>
        Nedostali jste kód?<br />
        <Link onClick={handleEnrollForReceipt}>Znovu odeslat kód na {phone}</Link><br />
        <Link onClick={handleReset}>Zadat jiné telefonní číslo</Link>
      </p>
    </CardBody>
  );

  const cardBody = !codeSent
    ? cardBodyForEnroll
    : cardBodyForCodeVerification;

  return (<>
    <Header />
    <Container compact={true}>
      <AlignContent to='center'>
        <Card className={classes.verificationCard}>
          <CardTitle>
            <strong>
              {(() => {
                if (requestType === RequestType.ReceiptOrder) {
                  return 'Ověření pacienta IKEM';
                } else {
                  return 'Ověření kontaktních údajů';
                }
              })()}
            </strong>
          </CardTitle>

          <CardBody>
            {cardBody}
          </CardBody>
        </Card>
      </AlignContent>
    </Container>
  </>);
};
